import { useEffect } from 'react'

import { useLocation } from 'react-router-dom'
import { useLocalUserPreference } from '../useLocalUserPreference/useLocalUserPreference'

export const UTM_PARAMS_STORAGE_KEY = 'utmParams'
export const UTM_PARAMS_EXPIRATION_HOURS = 24 * 30

export type UtmParams = {
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  utmTerm: string | null
  utmContent: string | null
}

export function parseUtmParamsFromSearch(search: string): UtmParams | null {
  const urlParams = new URLSearchParams(search)
  const getParam = (name: string) => urlParams.get(name) || null

  const utmParams: UtmParams = {
    utmSource: getParam('utm_source'),
    utmMedium: getParam('utm_medium'),
    utmCampaign: getParam('utm_campaign'),
    utmTerm: getParam('utm_term'),
    utmContent: getParam('utm_content')
  }

  return Object.values(utmParams).some(param => param !== null) ? utmParams : null
}

export function mergeUtmParams(preferred: UtmParams | null, fallback: UtmParams | null): UtmParams | null {
  if (!preferred) {
    return fallback
  }

  if (!fallback) {
    return preferred
  }

  return {
    utmSource: preferred.utmSource || fallback.utmSource || null,
    utmMedium: preferred.utmMedium || fallback.utmMedium || null,
    utmCampaign: preferred.utmCampaign || fallback.utmCampaign || null,
    utmTerm: preferred.utmTerm || fallback.utmTerm || null,
    utmContent: preferred.utmContent || fallback.utmContent || null
  }
}

export function parseStoredUtmParams(stored: string | null): UtmParams | null {
  if (!stored) {
    return null
  }

  try {
    return JSON.parse(stored) as UtmParams
  } catch (error) {
    return null
  }
}

export function readStoredUtmParams(): UtmParams | null {
  const stored = localStorage.getItem(`preference_${UTM_PARAMS_STORAGE_KEY}`)

  if (!stored) {
    return null
  }

  try {
    const { value, expiresAt } = JSON.parse(stored) as { value: string; expiresAt: number }

    if (expiresAt !== -1 && new Date().getTime() > expiresAt) {
      return null
    }

    return parseStoredUtmParams(value)
  } catch (error) {
    return null
  }
}

export function useUtmParams() {
  const [utmParams, setUtmParams] = useLocalUserPreference(UTM_PARAMS_STORAGE_KEY)
  const { search } = useLocation()

  useEffect(() => {
    const utmParams = parseUtmParamsFromSearch(search)

    if (utmParams) {
      setUtmParams(JSON.stringify(utmParams), { expiresInHours: UTM_PARAMS_EXPIRATION_HOURS })

      return
    }
  }, [search, setUtmParams])

  return {
    utmParams: parseStoredUtmParams(utmParams)
  }
}
